import React, { useState, useEffect, useMemo, useRef, forwardRef } from 'react';
import { AttachmentAudioLeftIcon, AttachmentDocumentLeftIcon, AttachmentFileTypeCard, AttachmentMediaModal, AttachmentSourcesPreview, type AttachmentMediaOverlayState, CtyButton, CtySpinner, SpeechUtility, SourceElementType, ClaimDataRenderer, ClaimScoreGeneratorRenderer, ScoreThresholdsProvider, useAgentScoreThresholds, formatElapsedTimeLabel, ID_CONSTANTS, getAttachmentPreviewKindFromFile, getDocumentCategoryLabelFromFile, openLocalFileInNewTab, useObjectUrl, CommandFormReadOnly } from '@circuitry-ai/doc-ui';
import { Message, RootState, useAppDispatch, SETTING_TYPE, sendStreamingMessage } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import { ChatOptions } from './chat-options';
import UnifiedMediaPreview from './unified-media-preview';
import { ChatLogo } from './chatlogo';
import ExplainAdvisorAnswer from './explainanswer/explainadvisoranswer';
import ExplainPartsCatalogAnswer from './explainanswer/explainPartsCatalogAnswer';
import KnowledgeSourceUI from './knowledgeSourceUI';
import { Icon } from '@iconify/react';
import { ChatMessageResponse } from './chatmessageresponse';
import { Modal, ModalContent, ModalHeader, ModalBody, Accordion, AccordionItem } from '@nextui-org/react';
import MetadataDisplay from './metadata-display';
import { ChatMessageHoverActions } from './ChatMessageHoverActions';
import { AdvisorThinkingProcess } from './AdvisorThinkingProcess';

import 'viewerjs/dist/viewer.css';
import Viewer from 'viewerjs';

const VIEWER_OPTIONS = { title: false, toolbar: true, navbar: true, movable: true, zoomable: true, rotatable: true };

/**
 * Renders a single claim's score/data with agent-config driven score color thresholds.
 * Needs its own component (not inline) so the per-claim `useAgentScoreThresholds` hook can run
 * inside the claims `.map()`. Cached per aiworker_id by RTK Query; falls back to 85/60 defaults.
 */
const ClaimScoreThresholdRenderer: React.FC<{
  claim: any;
  isClaimScore: boolean;
  showActionButtons: boolean;
  className?: string;
}> = ({ claim, isClaimScore, showActionButtons, className }) => {
  const { thresholds: scoreThresholds, isReady: areScoreThresholdsReady } = useAgentScoreThresholds(claim?.aiworker_id);
  if (!areScoreThresholdsReady) {
    return <CtySpinner size="sm" />;
  }
  return (
    <ScoreThresholdsProvider value={scoreThresholds}>
      {isClaimScore ? (
        <ClaimScoreGeneratorRenderer
          claimData={[claim]}
          showActionButtons={showActionButtons}
          source="agent_history"
          className={className}
        />
      ) : (
        <ClaimDataRenderer
          claimData={[claim]}
          showActionButtons={showActionButtons}
          className={className}
        />
      )}
    </ScoreThresholdsProvider>
  );
};

function useImageViewer(imageUrls: string[]) {
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<InstanceType<typeof Viewer> | null>(null);
  useEffect(() => {
    if (imageUrls.length === 0 || !viewerContainerRef.current) return;
    const timerId = setTimeout(() => {
      if (!viewerContainerRef.current) return;
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      try {
        viewerRef.current = new Viewer(viewerContainerRef.current, VIEWER_OPTIONS);
      } catch (err) {
        console.warn('Viewer init failed:', err);
      }
    }, 100);
    return () => {
      clearTimeout(timerId);
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [imageUrls]);
  const openEnlargedAt = (index: number) => {
    if (viewerRef.current) {
      try {
        viewerRef.current.view(index);
      } catch (err) {
        console.warn('Viewer view failed:', err);
      }
    }
  };
  return { viewerContainerRef, openEnlargedAt };
}

function ImageThumbnailStrip(props: {
  urls: string[];
  viewerContainerRef: React.RefObject<HTMLDivElement | null>;
  openEnlargedAt: (index: number) => void;
}) {
  const { urls, viewerContainerRef, openEnlargedAt } = props;
  if (urls.length === 0) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2 mb-2 mt-1">
        {urls.map((url, i) => (
          <button
            key={url}
            type="button"
            onClick={() => openEnlargedAt(i)}
            className="w-12 h-12 rounded-md border border-default-200 overflow-hidden bg-default-100 flex-shrink-0"
          >
            <img src={url} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      <div ref={viewerContainerRef as React.RefObject<HTMLDivElement>} className="hidden" aria-hidden="true">
        {urls.map((url) => (
          <img key={url} src={url} alt="" />
        ))}
      </div>
    </>
  );
}

export interface ChatMetaData {
  isAdvisor: boolean;
  initials?: string;
  username: string;
  textMessage: string;
  messageId: string;
  viewKnowledge: boolean;
  advisorLogo?: string;
  viewExpertAnswer: boolean;
  figure_refs?: string[];
  isStreaming?: boolean;
  isAgent?: boolean;
  agentName?: string;
  /** Formatted time the message was sent. Only history views pass this. */
  timestampLabel?: string;
}

// Extended message interface for type safety
interface ExtendedMessage extends Message {
  isClaimData?: boolean;
  claimData?: any;
  agentName?: string;
  intent_and_entities?: any;
  advisor_name?: string;
  associatedKnowledgeArticles?: Array<{
    article_id: string;
    article_name: string;
    article_status: string;
    article_type: string;
  }>;
  thinkingProcesses?: Array<{
    type: string;
    message: string;
    duration_sec?: number;
  }>;
  phases?: Array<{
    type: string;
    message: string;
    node_id?: string;
    duration_sec?: number;
  }>;
  attachedImageMeta?: { imagesourceId: string[]; imagetype: string[] };
  attachedImageFiles?: File[];
  /** For conversation log: user message sources from API (source_id, file_name) to show images via presigned URLs */
  sources?: { source_id: string; file_name: string }[];
}
interface CommonChatMessagesProps {
  readonly chatMetaData: ChatMetaData;
  readonly msg: Partial<ExtendedMessage>;
  readonly isAdvisorView: boolean;
  readonly isSpeechMode?: boolean;
  readonly expandView: boolean;
  readonly chatAdvisor: any;
  readonly msgIndex: number;
  readonly getAdvisorFeedbackRefetch?: () => void;
  readonly setIsSpeechMode: (value: boolean) => void;
  readonly onDeepThinkRegenerate?: () => void;
  readonly isLatestAdvisorMessage?: boolean;
  readonly converseSenderId?: string;
  /** When set (e.g. conversation log), presigned URLs are provided by parent; no per-message API call */
  readonly presignedUrlsBySourceId?: Record<string, string>;
}


export const CommonChatMessages = forwardRef<HTMLParagraphElement, Readonly<CommonChatMessagesProps>>((props, ref) => {

  const { token } = useSelector((state: RootState) => state.auth);
  const { advisorAbilities } = useSelector((state: RootState) => state.advisor);
  const chatMessages = useSelector((state: RootState) => state.chatAdvisorSliceReducer.chatMessages);
  const currentChatMessageUploadedImageInfo = useSelector((state: RootState) => state.chatAdvisorSliceReducer.currentChatMessageUploadedImageInfo);
  const currentChatMentionedEntities = useSelector((state: RootState) => state.chatAdvisorSliceReducer.currentChatMentionedEntities);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth.user);
  const { apiKey: guestApiKey } = useSelector((state: RootState) => state.advisor.guestToken);

  const { chatMetaData, msg, isAdvisorView, isSpeechMode, chatAdvisor, msgIndex, setIsSpeechMode } = props;
  const dispatch = useAppDispatch();
  const [translatedText, setTranslatedText] = useState<string>(() => chatMetaData.textMessage ?? '');
  const currLanguage = SpeechUtility.getLanguage();
  const [inlineTriggerSource, setInlineTriggerSource] = useState<any>(null);
  const [inlineTriggerLoading, setInlineTriggerLoading] = useState<string>('');
  const [_triggerInlineFileView, setTriggerInlineFileView] = useState<boolean>(false);
  const [showClaimDataModal, setShowClaimDataModal] = useState<boolean>(false);

  const transformText = async (userInput: string) => {
    if (currLanguage === 'en-US') {
      setTranslatedText(userInput);
      return;
    }
    try {
      const speechUtilityInstance = await SpeechUtility.getInstance(dispatch);
      const transFormedText = await speechUtilityInstance.translateText(userInput, currLanguage);
      setTranslatedText(transFormedText?.trim() ? transFormedText : userInput);
    } catch {
      setTranslatedText(userInput);
    }
  };

  useEffect(() => {
    const text = chatMetaData.textMessage ?? '';
    if (!text.trim()) {
      setTranslatedText('');
      return;
    }
    // User-authored text is shown as-is; only advisor replies are localized for the viewer.
    if (isAdvisorView && chatMetaData.isAdvisor) {
      void transformText(text);
    } else {
      setTranslatedText(text);
    }
  }, [chatMetaData.textMessage, chatMetaData.isAdvisor, isAdvisorView]);

  // Handle streaming message updates
  useEffect(() => {
    if (chatMetaData.isStreaming && msg?.message) {
      if (isAdvisorView && chatMetaData.isAdvisor) {
        void transformText(msg.message);
      } else {
        setTranslatedText(msg.message);
      }
    }
  }, [msg?.message, chatMetaData.isStreaming, chatMetaData.isAdvisor, isAdvisorView]);

  const metadataMap = useMemo(() => {
    // start with an empty map
    let baseMap: Record<string, any> = {};

    // build the "base" map from the array or object
    if (Array.isArray(msg.metaData)) {
      baseMap = msg.metaData.reduce((acc: any, obj: any) => {
        const [key, value] = Object.entries(obj)[0] || [];
        if (key) acc[key] = value;
        return acc;
      }, {});
    } else if (msg.metaData && typeof msg.metaData === 'object') {
      // if it's already an object, just shallow-copy it
      baseMap = { ...msg.metaData };
    }
    // else leave baseMap as {}

    // now merge in intent_and_entities, if present
    if (msg.intent_and_entities) {
      baseMap['intent_and_entities'] = msg?.intent_and_entities;
    }

    return baseMap;
  }, [msg?.metaData, msg?.intent_and_entities]);

  // Extract accordion rendering logic to reduce duplication
  const renderClaimAccordion = (showActionButtons: boolean) => {
    return (
      <Accordion
        selectionMode="multiple"
        className=""
        itemClasses={{
          base: "bg-content1 rounded-lg shadow-sm mb-2",
          title: "font-normal",
          trigger: "px-4 py-3 data-[hover=true]:bg-default-100",
          content: "px-4 pb-4"
        }}
        dividerProps={{
          className: "hidden"
        }}
      >
        {Array.isArray(msg?.claimData) ? msg.claimData.map((claim, index) => {
          const agentScore = claim.agent_score === 'N/A' ? null : claim.agent_score;
          const isClaimScore = claim?.agent_name?.toLowerCase().includes("claim score") ||
            claim?.input_data?.aiworker_name?.toLowerCase().includes("claim score") ||
            claim?.aiworker_name?.toLowerCase().includes("claim score");

          return (
            <AccordionItem
              key={claim.aiworker_usage_id || index}
              aria-label={`${claim.entity_number} - ${claim.agent_name}`}
              title={
                <div className="flex justify-between items-center w-full">
                  <div className="flex flex-col">
                    <span className="text-foreground">{claim.entity_number}</span>
                    <span className="text-default-500 text-sm">{claim.agent_name}</span>
                  </div>
                  <div className="flex items-center gap-1 mr-2">
                    {agentScore !== null && (
                      <div className="text-sm">
                        Score: <span className="text-primary">{agentScore}%</span>
                      </div>
                    )}
                  </div>
                </div>
              }
              indicator={({ isOpen }) => (
                <Icon
                  icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                  className="text-default-400"
                  width="20"
                  height="20"
                />
              )}
            >
              <ClaimScoreThresholdRenderer
                claim={claim}
                isClaimScore={Boolean(isClaimScore)}
                showActionButtons={showActionButtons}
                className={showActionButtons ? "max-h-[500px] overflow-auto" : "max-h-96 overflow-auto"}
              />
            </AccordionItem>
          );
        }) : null}
      </Accordion>
    );
  };

  const renderKnowledgeArticleIndicator = () => {
    const knowledgeArticles = msg?.associatedKnowledgeArticles || [];

    if (knowledgeArticles.length === 0) {
      return null;
    }

    const expertAnswerArticles = knowledgeArticles.filter(ka => ka.article_type === 'expert_answer');

    if (expertAnswerArticles.length === 0) {
      return null;
    }

    return (
      <CtyButton
        tooltipContent={`${expertAnswerArticles.length} Knowledge Article${expertAnswerArticles.length > 1 ? 's' : ''} associated with this response`}
        toolTipColor='default'
        className="h-[32px] ml-2"
        isIconOnly
        radius="full"
        size="sm"
        variant="light"
      >
        <div className="relative">
          <Icon icon="mdi:file-document-multiple" width="1.1rem" height="1.1rem" style={{ color: "#0BA5EC" }} />
          {expertAnswerArticles.length > 1 && (
            <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
              {expertAnswerArticles.length}
            </span>
          )}
        </div>
      </CtyButton>
    );
  };

  const isTipsMessage = metadataMap?.message_type === 'TIPS';
  const isEnableExplainer = metadataMap?.enable_explainer ?? false;
  const isAdvisorResponseExlainerEnabled = isEnableExplainer && chatMetaData?.isAdvisor && !chatMetaData?.isAgent && !isTipsMessage && !chatMetaData?.isStreaming && msg?.metaData && (advisorAbilities[SETTING_TYPE.EXPLAIN_ANSWER]?.is_active ?? false);
  const isAdvisorResponseMediaPreviewEnabled = chatMetaData?.viewKnowledge && ((advisorAbilities[SETTING_TYPE.IMAGES]?.is_active ?? false) || (advisorAbilities[SETTING_TYPE.VIDEO]?.is_active ?? false) || (advisorAbilities[SETTING_TYPE.AUDIO]?.is_active ?? false));
  const isAdvisorResponseViewKnolwedgeEnabled = chatMetaData?.viewKnowledge && metadataMap?.source_ids[0]?.source_id;
  const isChatOptionsEnabled = !chatMetaData?.isStreaming && !isTipsMessage;
  const isResponseInfoEnabled = chatMetaData?.isAdvisor && metadataMap?.message_id && token;
  const isPartsCatelogEnabled = chatMetaData?.viewKnowledge && (
    (metadataMap?.source_ids[0]?.element_type === SourceElementType.PARTS_ZIP && metadataMap?.source_ids[0]?.zip_components_details?.length > 0) ||
    metadataMap?.source_ids[0]?.element_type === (SourceElementType.PARTS_SVG)
  );

  const isSvgEnabled = metadataMap?.source_ids?.[0]?.element_type === SourceElementType.SVG;

  // Determine if this is a parts catalog advisor based on the response structure or advisor type
  const isPartsCatalogExplainer = isAdvisorResponseExlainerEnabled && (
    isPartsCatelogEnabled || isSvgEnabled ||
    // Check if the message contains parts catalog specific data structure
    (msg?.explainanswer && typeof msg.explainanswer === 'object' &&
      (msg.explainanswer.retrieved_context?.headers || msg.explainanswer.reranked_context?.headers))
  );

  const getProcessingTime = () => {
    const totalTimeTaken = metadataMap?.total_time_taken_seconds;
    if (!totalTimeTaken) return 0;
    return totalTimeTaken;
  };

  const knowledgeSourceText = `Answered in ${formatElapsedTimeLabel(getProcessingTime())} based on the following sources`;

  // Regenerate handler - similar to deep think but without removing previous message
  const handleRegenerate = async (sourceIds: string[]) => {
    // Find the last user message (same as deep think)
    const lastUserMessage = [...chatMessages].reverse().find(msg => msg.isUser)?.message;
    if (!lastUserMessage) return;

    // Get API URL from chatAdvisor
    const chat_api = chatAdvisor?.chat_api;
    if (!chat_api) {
      console.error('Chat API URL not available');
      return;
    }

    // Get senderId from message metadata or use default
    const senderId = msg?.metaData?.[2]?.session_id || '';

    const sources = currentChatMessageUploadedImageInfo?.source_id
      ? [{ source_id: currentChatMessageUploadedImageInfo.source_id, file_name: currentChatMessageUploadedImageInfo.file_name || '' }]
      : [];
    await sendStreamingMessage(
      chat_api + 'api/v1/chat',
      lastUserMessage,
      senderId,
      chatAdvisor.advisor_id,
      sources,
      { ...currentChatMentionedEntities },
      token || '',
      dispatch,
      isAuthenticated ? undefined : guestApiKey,
      false, // deepThinkMode - false for regenerate
      isAuthenticated,
      undefined, // fetchLatestGuestToken - not available in this context
    );
  };

  const chatOptionsProps = {
    message: msg,
    translatedText,
    chatAdvisor,
    isSpeechMode: isSpeechMode ?? false,
    isAdvisorView,
    getAdvisorFeedbackRefetch: props?.getAdvisorFeedbackRefetch,
    setIsSpeechMode: setIsSpeechMode,
    msgIndex,
    converseSenderId: props.converseSenderId,
  };

  const isUserMessage = !chatMetaData.isAdvisor;
  const userDisplayText = useMemo(
    () => (translatedText || chatMetaData.textMessage || msg?.message || '').trim(),
    [translatedText, chatMetaData.textMessage, msg?.message]
  );

  const renderThinkingProcess = () => {
    const phases = Array.isArray(msg?.phases) ? msg.phases : null;
    const thinkingProcesses = Array.isArray(msg?.thinkingProcesses) ? msg.thinkingProcesses : null;
    const hasAgentPhases = !!chatMetaData.isAgent && (
      !!phases?.length || !!chatMetaData.isStreaming
    );
    const hasAdvisorThinking = chatMetaData.isAdvisor && !chatMetaData.isAgent && (
      !!thinkingProcesses?.length || !!chatMetaData.isStreaming
    );
    if (!hasAgentPhases && !hasAdvisorThinking) return null;

    const processes = hasAgentPhases ? (phases ?? []) : (thinkingProcesses ?? []);
    const hasResponse = !!msg?.message && msg.message.trim().length > 0;
    const isComplete = !chatMetaData.isStreaming && hasResponse;

    return (
      <AdvisorThinkingProcess
        processes={processes}
        isComplete={isComplete}
        isStreaming={!!chatMetaData.isStreaming}
        variant={hasAgentPhases ? 'agent' : 'advisor'}
      />
    );
  };

  const renderUserAttachments = () => (
    <>
      {(msg as Partial<ExtendedMessage>)?.attachedImageFiles?.length ? (
        <UserMessageAttachedImagesBlob files={(msg as Partial<ExtendedMessage>).attachedImageFiles ?? []} />
      ) : (msg as Partial<ExtendedMessage>)?.sources?.length ? (
        <AttachmentSourcesPreview sources={(msg as Partial<ExtendedMessage>).sources ?? []} presignedUrls={props.presignedUrlsBySourceId} />
      ) : null}
      <UserMessageCommandFormAttachment msg={msg} />
    </>
  );

  const renderMessageBody = () => (
    <>
      {!isUserMessage && (msg as Partial<ExtendedMessage>)?.attachedImageFiles?.length ? (
        <UserMessageAttachedImagesBlob files={(msg as Partial<ExtendedMessage>).attachedImageFiles ?? []} />
      ) : !isUserMessage && (msg as Partial<ExtendedMessage>)?.sources?.length ? (
        <AttachmentSourcesPreview sources={(msg as Partial<ExtendedMessage>).sources ?? []} presignedUrls={props.presignedUrlsBySourceId} />
      ) : null}
      {!isUserMessage ? <UserMessageCommandFormAttachment msg={msg} /> : null}
      {msg?.isClaimData && msg?.claimData ? (
        <div className="bg-default-100 rounded-lg overflow-hidden relative">
          <CtyButton
            isIconOnly
            size="sm"
            variant="light"
            className="absolute top-4 right-4 z-10 bg-background/80 backdrop-blur-sm"
            onPress={() => setShowClaimDataModal(true)}
            tooltipContent="View Full Screen"
          >
            <Icon icon="material-symbols:fullscreen" width="16" height="16" />
          </CtyButton>
          <div className="p-6">
            <div className="text-base font-semibold mb-4">
              Claim Information ({Array.isArray(msg?.claimData) ? msg.claimData.length : 0} claims)
            </div>
            {renderClaimAccordion(false)}
          </div>
        </div>
      ) : isUserMessage ? null : (
        <>
          <ChatMessageResponse
            msg={msg}
            messageId={chatMetaData?.messageId}
            advisorId={chatAdvisor.advisor_id}
            translatedText={
              chatMetaData.isStreaming
                ? (translatedText || msg?.message || '')
                : translatedText
            }
            isPartsCatelogEnabled={isPartsCatelogEnabled}
            setInlineTriggerSource={setInlineTriggerSource}
            setInlineTriggerLoading={setInlineTriggerLoading}
            inlineTriggerLoading={inlineTriggerLoading}
            setTriggerInlineFileView={setTriggerInlineFileView}
            onRegenerate={handleRegenerate}
            isLatestAdvisorMessage={props.isLatestAdvisorMessage}
          />
          {isAdvisorResponseMediaPreviewEnabled && <UnifiedMediaPreview msg={msg} advisorId={chatAdvisor.advisor_id} />}
          {isPartsCatalogExplainer ? (
            <ExplainPartsCatalogAnswer
              msgIndex={msgIndex}
              userQuery={msg?.explainanswer?.question ?? ''}
              metadataMap={metadataMap}
              advisorResponse={translatedText}
            />
          ) : (
            isAdvisorResponseExlainerEnabled && <ExplainAdvisorAnswer msgIndex={msgIndex} userQuery={msg?.explainanswer?.question ?? ''} metadataMap={metadataMap} advisorResponse={translatedText} />
          )}
          {metadataMap?.total_time_taken_seconds && (
            <p ref={ref} id={ID_CONSTANTS.knowledgeSourceText} className='text-base font-normal leading-5 text-default-500 mt-3'>{knowledgeSourceText}</p>
          )}
          {isAdvisorResponseViewKnolwedgeEnabled && (
            <KnowledgeSourceUI
              metadataMap={metadataMap}
              advisorId={chatAdvisor.advisor_id}
              inlineTriggerSource={inlineTriggerSource}
              inlineTriggerLoading={inlineTriggerLoading}
              setInlineTriggerLoading={setInlineTriggerLoading}
              hideSources={metadataMap?.block_bottom_sources}
            />
          )}
        </>
      )}
    </>
  );

  if (isUserMessage) {
    return (
      <div className="flex justify-end w-full max-w-full min-w-0 group/msg">
        <div className="flex flex-row-reverse items-start gap-2.5 max-w-[80%] min-w-0">
          <div className="w-[30px] h-[30px] rounded-full bg-default-500 text-white flex items-center justify-center text-[10px] font-extrabold shrink-0 mt-0.5">
            {chatMetaData.initials || 'U'}
          </div>
          <div className="flex flex-col items-end gap-1 min-w-0">
            <div className="text-[15px] font-bold text-foreground mt-2.5 mb-0.5 text-right">
              {chatMetaData.username}
            </div>
            {renderUserAttachments()}
            {userDisplayText ? (
              <div className="rounded-[14px] rounded-br-[4px] bg-[#e5f3ff] dark:bg-[#1a2f4a] px-[15px] py-[11px] min-w-0 max-w-full">
                <p className="text-[13.5px] leading-relaxed text-foreground whitespace-pre-wrap">
                  {userDisplayText}
                </p>
              </div>
            ) : null}
            <ChatMessageHoverActions
              text={userDisplayText}
              isAdvisorView={isAdvisorView}
              variant="below"
              timestamp={chatMetaData.timestampLabel}
            />
          </div>
        </div>
      </div>
    );
  }

  const hasResponseText = !!msg?.message && msg.message.trim().length > 0;

  return (
    <div className="flex flex-col w-full max-w-full min-w-0 shrink-0 group/msg">
      <div className="flex flex-row items-center gap-3">
        <ChatLogo viewLogo={chatMetaData.isAdvisor} advisorLogo={chatMetaData?.advisorLogo} initials={chatMetaData?.initials} />
        <div className="text-base font-bold text-foreground first-letter:uppercase flex flex-wrap items-center">
          <div>
            {chatMetaData.isAgent ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <Icon icon="mingcute:robot-2-fill" width="10" height="10" className="text-white" />
                </div>
                <span>{chatMetaData.agentName || 'Agent'}</span>
              </span>
            ) : (
              <span>{msg?.advisor_name && msg?.advisor_name.length > 0 ? msg?.advisor_name : chatMetaData.username}</span>
            )}
          </div>
          {isResponseInfoEnabled && <CtyButton tooltipContent={`${metadataMap?.agent_name ?? ''}: ${metadataMap?.message_id ?? ''}`} toolTipColor='warning' className="h-[32px]" isIconOnly radius="full" size="sm" variant="light">
            <div className="relative">
              <Icon icon="ph:info-light" width="1.2rem" height="1.1rem" style={{ color: "#afa4a4" }} />
            </div>
          </CtyButton>}
          {renderKnowledgeArticleIndicator()}
        </div>
      </div>

      <div className="flex-1 min-w-0 max-w-full relative flex flex-col gap-1 p-2 mt-1">
        {renderThinkingProcess()}

        {/* Display metadata information for advisor responses */}
        {chatMetaData.isAdvisor && !chatMetaData.isStreaming && !isTipsMessage && (
          <MetadataDisplay metadataMap={metadataMap.intent_and_entities} className="mt-1" />
        )}

        <div className="w-full min-w-0 break-words">
          {renderMessageBody()}
        </div>
        {isChatOptionsEnabled && hasResponseText ? (
          <ChatOptions
            {...chatOptionsProps}
            onDeepThinkRegenerate={props.onDeepThinkRegenerate}
            isLatestAdvisorMessage={props.isLatestAdvisorMessage}
          />
        ) : null}
      </div>

      {msg?.isClaimData && msg?.claimData && (
        <Modal
          isOpen={showClaimDataModal}
          onOpenChange={setShowClaimDataModal}
          size="2xl"
          scrollBehavior="inside"
        >
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className="flex flex-col gap-1 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <Icon icon="mingcute:robot-2-fill" className="text-white w-5 h-5" />
                    </div>
                    <span className="text-primary font-semibold">Agent Log</span>
                    <span className="text-default-500">- Claim Information</span>
                  </div>
                </ModalHeader>

                <ModalBody className="p-6 bg-default-100">
                  <div className="text-base font-semibold mb-4">
                    Claim Information ({Array.isArray(msg?.claimData) ? msg.claimData.length : 0} claims)
                  </div>

                  {/* Render accordion using reusable function */}
                  {renderClaimAccordion(true)}
                </ModalBody>
              </>
            )}
          </ModalContent>
        </Modal>
      )}

    </div>
  );
});

CommonChatMessages.displayName = 'CommonChatMessages';

function ChatBlobVideoAttachmentCard(props: Readonly<{ file: File; videoLabel: string; onOpen: (url: string, name: string) => void }>) {
  const { file, videoLabel, onOpen } = props;
  const url = useObjectUrl(file);
  return (
    <AttachmentFileTypeCard
      label={videoLabel}
      fileName={file.name}
      loading={!url}
      onClick={() => url && onOpen(url, file.name)}
      leftVariant="media"
      leftContent={
        url ? (
          <video src={url} className="w-full h-full object-cover pointer-events-none" muted playsInline preload="metadata" />
        ) : null
      }
    />
  );
}

function ChatBlobAudioAttachmentCard(props: Readonly<{ file: File; audioLabel: string; onOpen: (url: string, name: string) => void }>) {
  const { file, audioLabel, onOpen } = props;
  const url = useObjectUrl(file);
  return (
    <AttachmentFileTypeCard
      label={audioLabel}
      fileName={file.name}
      loading={!url}
      onClick={() => url && onOpen(url, file.name)}
      leftVariant="neutral"
      leftContent={<AttachmentAudioLeftIcon />}
    />
  );
}

function UserMessageCommandFormAttachment({ msg }: Readonly<{ msg: Partial<ExtendedMessage> }>) {
  const formData = (msg as any)?.commandFormData as Record<string, string> | undefined;
  const customMessage = ((msg as any)?.commandCustomMessage ?? '') as string;
  const commandJson = (msg as any)?.commandJson as Record<string, any> | undefined;
  const aiworkerId = (msg as any)?.aiworkerId as string | undefined;

  if (!formData || !commandJson) return null;

  return (
    <div className="mb-1 w-[90%]">
      <CommandFormReadOnly
        commandJson={commandJson}
        formData={formData}
        customMessage={customMessage}
        aiworkerId={aiworkerId}
      />
    </div>
  );
}

/** Renders attached files (images, video, audio, documents) using blob URLs from File[]. */
function UserMessageAttachedImagesBlob(props: Readonly<{ files: File[] }>) {
  const { files } = props;
  const [mediaOverlay, setMediaOverlay] = useState<AttachmentMediaOverlayState>(null);
  const imageFiles = useMemo(() => files.filter((f) => getAttachmentPreviewKindFromFile(f) === 'image'), [files]);
  const [blobUrls, setBlobUrls] = useState<string[]>([]);
  const { viewerContainerRef, openEnlargedAt } = useImageViewer(blobUrls);

  useEffect(() => {
    if (!imageFiles.length) {
      setBlobUrls([]);
      return;
    }
    const urls = imageFiles.map((f) => URL.createObjectURL(f));
    setBlobUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
      setBlobUrls([]);
    };
  }, [imageFiles]);

  if (!files.length) return null;

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-2 mt-1 items-start">
        {files.map((file) => {
          const kind = getAttachmentPreviewKindFromFile(file);
          const key = `${file.name}-${file.size}-${file.lastModified}`;
          if (kind === 'image') {
            const imageIndex = imageFiles.indexOf(file);
            const url = blobUrls[imageIndex];
            return (
              <button
                key={key}
                type="button"
                onClick={() => url && openEnlargedAt(imageIndex)}
                className="w-12 h-12 rounded-md border border-default-200 overflow-hidden bg-default-100 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <CtySpinner size="sm" />}
              </button>
            );
          }
          if (kind === 'video') {
            return (
              <ChatBlobVideoAttachmentCard
                key={key}
                file={file}
                videoLabel="Video"
                onOpen={(url, name) => setMediaOverlay({ kind: 'video', url, name })}
              />
            );
          }
          if (kind === 'audio') {
            return (
              <ChatBlobAudioAttachmentCard
                key={key}
                file={file}
                audioLabel="Audio"
                onOpen={(url, name) => setMediaOverlay({ kind: 'audio', url, name })}
              />
            );
          }
          return (
            <AttachmentFileTypeCard
              key={key}
              label={getDocumentCategoryLabelFromFile(file)}
              fileName={file.name}
              loading={false}
              onClick={() => openLocalFileInNewTab(file)}
              leftVariant="neutral"
              leftContent={<AttachmentDocumentLeftIcon />}
            />
          );
        })}
      </div>
      {blobUrls.length > 0 && (
        <div ref={viewerContainerRef as React.RefObject<HTMLDivElement>} className="hidden" aria-hidden="true">
          {blobUrls.map((url) => (
            <img key={url} src={url} alt="" />
          ))}
        </div>
      )}
      <AttachmentMediaModal overlay={mediaOverlay} onClose={() => setMediaOverlay(null)} />
    </>
  );
}


export default CommonChatMessages;
